/*
 * Careers page.
 *
 * A port of the Nuxt careers pages
 * (Web/src/stores/premium/pages/career/index.vue and .../[slug]/index.vue) onto
 * this theme, reading the SAME source of truth: the public HR dataset in Sanity
 * project 134znqxr. HR edits a posting once in the studio and both storefronts
 * show it - nothing is copied into Shopify and nothing has to be re-synced.
 *
 * The dataset is public-read and holds nothing but job postings, so there is
 * deliberately no token here. Applications go to a Google Form
 * (`application_form_url`), never to Sanity and never to this page - do not add
 * a form or a token, this file is served to the browser.
 *
 * Sanity enforces a CORS origin allowlist, so every host this page is served
 * from must be listed under sanity.io/manage -> API -> CORS origins. Without it
 * the fetch fails and the page shows its load error. See the section comment.
 *
 * Nuxt gives the Web version a route per job; Liquid has no dynamic routes, so
 * both views live on this one page and `?job=<slug>` picks between them. Cards
 * are real <a> links to that URL - middle-click and copy-link work - and the
 * click handler only upgrades the navigation to pushState. Back and forward go
 * through popstate into the same render().
 *
 * One query serves both views. The list is small (a handful of postings, a few
 * KB) and the detail view needs nothing the list query does not already carry,
 * so a second round trip per job would buy nothing.
 */
(function () {
  var root = document.querySelector('[data-gb-careers]');
  if (!root) return;

  var mount = root.querySelector('[data-gb-careers-mount]');
  var intro = root.querySelector('[data-gb-careers-intro]');
  var i18nNode = root.querySelector('[data-gb-careers-i18n]');
  var configNode = root.querySelector('[data-gb-careers-config]');
  if (!mount || !intro || !i18nNode || !configNode) return;

  /* Shopify's `t` filter HTML-escapes its output, so the JSON blob arrives with
     entities already in it. Decode once here so the in-memory strings are plain
     text and escape() stays the single escaping point - same reasoning as
     gb-returns.js. */
  var i18n = decodeEntities(JSON.parse(i18nNode.textContent));
  var config = JSON.parse(configNode.textContent);

  /* Query kept in step with Web/src/composables/logic/sanity.ts. `role` and
     `countries` are not shown anywhere, but `role` is searched and `countries`
     feeds the JobPosting structured data. */
  var JOB_FIELDS =
    '_id,_createdAt,title,slug,category,role,short_description,description,' +
    'sections[]{_key,title,items},location,location_type,countries,application_form_url,order';

  /** A job with no `visible_countries` (or an empty one) is shown on every storefront. */
  var VISIBLE_IN_COUNTRY =
    '(!defined(visible_countries) || count(visible_countries) == 0 || $country in visible_countries)';

  var JOBS_QUERY =
    '*[_type == "job" && status == "active" && defined(slug.current) && ' +
    VISIBLE_IN_COUNTRY +
    '] | order(category asc, order asc, _createdAt asc){ ' +
    JOB_FIELDS +
    ' }';

  var LOCATION_TYPE_SCHEMA = { remote: 'TELECOMMUTE', hybrid: 'TELECOMMUTE' };

  var jobs = null;
  var searchText = '';
  var searchTimer = null;
  var listTitle = document.title;
  var applyObserver = null;

  /* Delegated once, on the container that survives every re-render. */
  root.addEventListener('click', onClick);
  window.addEventListener('popstate', render);

  load();

  function load() {
    mount.innerHTML = skeletonHtml();

    fetchJobs()
      .then(function (result) {
        jobs = result || [];
        render();
      })
      .catch(function () {
        jobs = null;
        renderError();
      });
  }

  function fetchJobs() {
    var url =
      'https://' + config.project + '.apicdn.sanity.io' +
      '/v' + config.apiVersion + '/data/query/' + config.dataset +
      '?query=' + encodeURIComponent(JOBS_QUERY) +
      '&$country=' + encodeURIComponent(JSON.stringify(config.country));

    return fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('sanity ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        return payload.result;
      });
  }

  /* ---------------------------------------------------------------- routing */

  function currentSlug() {
    return new URLSearchParams(window.location.search).get('job') || '';
  }

  /* Both URLs are built by editing the query string that is already there, not
     from a stored path: the theme editor and admin previews load this page with
     their own parameters, and dropping those on the first click would break the
     preview. */
  function jobUrl(slug) {
    var params = new URLSearchParams(window.location.search);
    params.set('job', slug);
    return window.location.pathname + '?' + params.toString();
  }

  function listUrl() {
    var params = new URLSearchParams(window.location.search);
    params.delete('job');
    var query = params.toString();
    return window.location.pathname + (query ? '?' + query : '');
  }

  function onClick(event) {
    var link = event.target.closest('[data-gb-careers-link]');
    if (link) {
      /* Leave modified clicks to the browser - they mean "somewhere else".
         `> 0` rather than `!== 0`: a keyboard activation has no button at all. */
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
      event.preventDefault();
      go(jobUrl(link.getAttribute('data-slug')));
      return;
    }

    if (event.target.closest('[data-gb-careers-back]')) {
      event.preventDefault();
      go(listUrl());
      return;
    }

    if (event.target.closest('[data-gb-careers-retry]')) {
      event.preventDefault();
      load();
    }
  }

  function go(url) {
    window.history.pushState({}, '', url);
    window.scrollTo({ top: 0, behavior: 'auto' });
    render();
  }

  function render() {
    if (jobs === null) return;

    var slug = currentSlug();
    var job = slug ? find(slug) : null;

    /* A slug with no match is a posting that has been closed or hidden from this
       storefront. Liquid cannot answer 404 from here, and stranding someone on
       an error is worse than showing what IS open, so this falls back to the
       list and drops the stale parameter from the URL. */
    if (slug && !job) window.history.replaceState({}, '', listUrl());

    if (job) renderJob(job);
    else renderList();
  }

  function find(slug) {
    for (var i = 0; i < jobs.length; i++) {
      if (jobs[i].slug && jobs[i].slug.current === slug) return jobs[i];
    }
    return null;
  }

  /* ------------------------------------------------------------------- list */

  function renderList() {
    leaveJobView();

    mount.innerHTML =
      '<section class="gb-careers__open">' +
        '<h2 class="gb-careers__h2">' + escape(i18n.open_positions) + '</h2>' +
        '<p class="gb-careers__intro-text">' + escape(i18n.open_positions_intro) + '</p>' +
        '<div class="gb-careers__search">' +
          '<input type="search" autocomplete="off" data-gb-careers-search' +
            ' placeholder="' + escape(i18n.search_placeholder) + '"' +
            ' aria-label="' + escape(i18n.search_placeholder) + '">' +
        '</div>' +
        '<div data-gb-careers-results></div>' +
      '</section>';

    var input = mount.querySelector('[data-gb-careers-search]');
    input.value = searchText;
    input.addEventListener('input', onSearch);

    renderResults();
  }

  /* Only the results block is redrawn while typing - redrawing the whole list
     would take the focus and the caret out of the input on every keystroke. */
  function onSearch(event) {
    var value = event.target.value;
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(function () {
      searchText = value;
      renderResults();
    }, 250);
  }

  function renderResults() {
    var results = mount.querySelector('[data-gb-careers-results]');
    if (!results) return;

    var groups = groupByCategory(filtered());

    if (!groups.length) {
      results.innerHTML =
        '<p class="gb-careers__empty">' +
        escape(searchText.trim() ? i18n.no_results : i18n.no_openings) +
        '</p>';
      return;
    }

    results.innerHTML = groups
      .map(function (group) {
        return (
          '<div class="gb-careers__group">' +
            '<h3 class="gb-careers__group-title">' + escape(group.label) + '</h3>' +
            '<div class="gb-careers__cards">' + group.jobs.map(cardHtml).join('') + '</div>' +
          '</div>'
        );
      })
      .join('');
  }

  function filtered() {
    var needle = searchText.trim().toLowerCase();
    if (!needle) return jobs;

    return jobs.filter(function (job) {
      return [job.title, job.role, job.short_description].some(function (field) {
        return field && field.toLowerCase().indexOf(needle) !== -1;
      });
    });
  }

  /* The query already orders by category then `order`, so one pass in order is
     enough to build the groups. */
  function groupByCategory(list) {
    var groups = [];

    list.forEach(function (job) {
      var category = job.category || 'other';
      var group = groups.filter(function (item) { return item.category === category; })[0];

      if (group) group.jobs.push(job);
      else groups.push({ category: category, label: categoryLabel(category), jobs: [job] });
    });

    return groups;
  }

  function categoryLabel(category) {
    return (i18n.category && i18n.category[category]) || humanise(category);
  }

  function locationTypeLabel(type) {
    if (!type) return '';
    return (i18n.location && i18n.location[type]) || humanise(type);
  }

  function humanise(value) {
    return String(value).split('_').join(' ');
  }

  function cardHtml(job) {
    var slug = job.slug.current;
    var meta = [];
    if (job.location) meta.push(escape(job.location));
    var type = locationTypeLabel(job.location_type);
    if (type) meta.push(escape(type));

    return (
      '<a class="gb-careers__card" href="' + escape(jobUrl(slug)) + '"' +
        ' data-gb-careers-link data-slug="' + escape(slug) + '">' +
        '<span class="gb-careers__card-main">' +
          '<span class="gb-careers__card-title">' + escape(job.title) + '</span>' +
          (job.short_description
            ? '<span class="gb-careers__card-sub">' + escape(job.short_description) + '</span>'
            : '') +
        '</span>' +
        (meta.length
          ? '<span class="gb-careers__card-meta">' + meta.join('<span class="gb-careers__dot">&middot;</span>') + '</span>'
          : '') +
        '<span class="gb-careers__card-cta">' + escape(i18n.view_job) + '<span aria-hidden="true">&rarr;</span></span>' +
      '</a>'
    );
  }

  function renderError() {
    leaveJobView();
    mount.innerHTML =
      '<div class="gb-careers__card-block gb-careers__error">' +
        '<p>' + escape(i18n.load_error) + '</p>' +
        '<button type="button" class="gb-careers__btn" data-gb-careers-retry>' +
          escape(i18n.retry) +
        '</button>' +
      '</div>';
  }

  function skeletonHtml() {
    return (
      '<div class="gb-careers__skeleton" aria-hidden="true">' +
        '<span></span><span></span><span></span>' +
      '</div>'
    );
  }

  /* ----------------------------------------------------------------- detail */

  function renderJob(job) {
    intro.hidden = true;
    document.title = job.title + ' | ' + config.brand.name;

    var applyUrl = safeHref(job.application_form_url);

    mount.innerHTML =
      '<article class="gb-careers__job">' +
        '<a class="gb-careers__back" href="' + escape(listUrl()) + '" data-gb-careers-back>' +
          '<span aria-hidden="true">&larr;</span>' + escape(i18n.back_to_jobs) +
        '</a>' +

        '<header class="gb-careers__job-head">' +
          '<p class="gb-careers__eyebrow">' + escape(i18n.join_us_as) + '</p>' +
          '<h1 class="gb-careers__job-title">' + escape(job.title) + '</h1>' +
          metaLineHtml(job) +
        '</header>' +

        '<div class="gb-careers__prose">' + portableText(job.description) + '</div>' +

        sectionsHtml(job.sections) +
        applyHtml(applyUrl) +
        contactHtml() +
      '</article>' +
      stickyHtml(applyUrl);

    setJsonLd(job);
    watchApplyButton();
  }

  function metaLineHtml(job) {
    var meta = [];
    if (job.location) meta.push(escape(job.location));
    var type = locationTypeLabel(job.location_type);
    if (type) meta.push(escape(type));
    if (!meta.length) return '';

    return '<p class="gb-careers__job-meta">' +
      meta.join('<span class="gb-careers__dot">&middot;</span>') + '</p>';
  }

  function sectionsHtml(sections) {
    if (!sections || !sections.length) return '';

    return (
      '<section class="gb-careers__work">' +
        '<h2 class="gb-careers__h2">' + escape(i18n.your_work_title) + '</h2>' +
        '<div class="gb-careers__work-body">' +
          sections
            .map(function (section) {
              return (
                '<div class="gb-careers__work-item">' +
                  '<h3>' + escape(section.title) + '</h3>' +
                  '<ul>' +
                    (section.items || [])
                      .map(function (item) { return '<li>' + escape(item) + '</li>'; })
                      .join('') +
                  '</ul>' +
                '</div>'
              );
            })
            .join('') +
        '</div>' +
      '</section>'
    );
  }

  function applyHtml(url) {
    if (!url) return '';

    return (
      '<section class="gb-careers__apply">' +
        '<h2 class="gb-careers__h2">' + escape(i18n.apply_title) + '</h2>' +
        '<p class="gb-careers__intro-text">' + escape(i18n.apply_intro) + '</p>' +
        '<a id="apply-now" class="gb-careers__btn" target="_blank" rel="noopener noreferrer"' +
          ' href="' + escape(url) + '">' + escape(i18n.apply_now) + '</a>' +
      '</section>'
    );
  }

  function contactHtml() {
    var email = config.contactEmail;

    return (
      '<section class="gb-careers__contact">' +
        '<p class="gb-careers__eyebrow">' + escape(i18n.contact.label) + '</p>' +
        '<h2>' + escape(i18n.contact.title) + '</h2>' +
        '<p class="gb-careers__contact-text">' + escape(i18n.contact.description) + '</p>' +
        '<p class="gb-careers__contact-line">' +
          '<span>' + escape(i18n.contact.email_us) + '</span>' +
          '<a href="mailto:' + escape(email) + '">' + escape(email) + '</a>' +
        '</p>' +
      '</section>'
    );
  }

  /* Mobile only: the real apply button is at the bottom of a long page, so a
     copy of it rides along until that one is on screen. */
  function stickyHtml(url) {
    if (!url) return '';

    return (
      '<div class="gb-careers__sticky" data-gb-careers-sticky>' +
        '<a class="gb-careers__btn gb-careers__btn--full" target="_blank" rel="noopener noreferrer"' +
          ' href="' + escape(url) + '">' + escape(i18n.apply_now) + '</a>' +
      '</div>'
    );
  }

  function watchApplyButton() {
    var sticky = root.querySelector('[data-gb-careers-sticky]');
    var applyButton = root.querySelector('#apply-now');
    if (!sticky || !applyButton || typeof IntersectionObserver === 'undefined') return;

    applyObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        sticky.classList.toggle('is-visible', !entry.isIntersecting);
      });
    });

    applyObserver.observe(applyButton);
  }

  /* Everything the job view added to the page, undone in one place - both exits
     from it (back to the list, and a failed reload) go through here. */
  function leaveJobView() {
    intro.hidden = false;
    document.title = listTitle;
    clearJsonLd();

    if (applyObserver) {
      applyObserver.disconnect();
      applyObserver = null;
    }
  }

  /* ------------------------------------------------------- structured data */

  function setJsonLd(job) {
    clearJsonLd();

    var data = {
      '@context': 'https://schema.org/',
      '@type': 'JobPosting',
      title: job.title || '',
      description: job.short_description || '',
      datePosted: job._createdAt || '',
      employmentType: 'FULL_TIME',
      hiringOrganization: {
        '@type': 'Organization',
        name: config.brand.name,
        sameAs: config.brand.url,
        logo: config.brand.logo,
      },
      directApply: false,
      url: window.location.href,
    };

    if (job.location) {
      data.jobLocation = {
        '@type': 'Place',
        address: { '@type': 'PostalAddress', addressLocality: job.location },
      };
    }

    if (LOCATION_TYPE_SCHEMA[job.location_type]) {
      data.jobLocationType = LOCATION_TYPE_SCHEMA[job.location_type];
    }

    if (job.countries && job.countries.length) {
      data.applicantLocationRequirements = job.countries.map(function (country) {
        return { '@type': 'Country', name: country };
      });
    }

    var node = document.createElement('script');
    node.type = 'application/ld+json';
    node.setAttribute('data-gb-careers-ld', '');
    node.textContent = JSON.stringify(data);
    document.head.appendChild(node);
  }

  function clearJsonLd() {
    var node = document.querySelector('[data-gb-careers-ld]');
    if (node) node.parentNode.removeChild(node);
  }

  /* ---------------------------------------------------------- portable text */

  /*
   * Renders the subset of Portable Text the `job.description` field allows
   * (studio-hr/schemaTypes/documents/job.ts): normal and h3 blocks,
   * bullet/numbered lists, strong/em decorators and link annotations. Same
   * subset as Web/.../career/portable-text.vue.
   */
  function portableText(blocks) {
    var html = '';
    var openList = null;

    (blocks || [])
      .filter(function (block) { return block && block._type === 'block'; })
      .forEach(function (block) {
        if (block.listItem) {
          var tag = block.listItem === 'number' ? 'ol' : 'ul';
          /* Consecutive items of the same kind collapse into one list. */
          if (openList !== tag) {
            if (openList) html += '</' + openList + '>';
            html += '<' + tag + '>';
            openList = tag;
          }
          html += '<li>' + spansHtml(block) + '</li>';
          return;
        }

        if (openList) {
          html += '</' + openList + '>';
          openList = null;
        }

        var element = block.style === 'h3' ? 'h3' : 'p';
        html += '<' + element + '>' + spansHtml(block) + '</' + element + '>';
      });

    if (openList) html += '</' + openList + '>';
    return html;
  }

  function spansHtml(block) {
    return (block.children || [])
      .map(function (span) {
        var text = escape(span.text);
        var marks = span.marks || [];

        if (marks.indexOf('strong') !== -1) text = '<strong>' + text + '</strong>';
        if (marks.indexOf('em') !== -1) text = '<em>' + text + '</em>';

        var href = safeHref(linkHref(block, span));
        if (href) {
          text = '<a href="' + escape(href) + '" target="_blank" rel="noopener noreferrer">' + text + '</a>';
        }

        return text;
      })
      .join('');
  }

  function linkHref(block, span) {
    var defs = block.markDefs || [];
    var marks = span.marks || [];

    for (var i = 0; i < marks.length; i++) {
      for (var j = 0; j < defs.length; j++) {
        if (defs[j]._key === marks[i] && defs[j]._type === 'link') return defs[j].href || null;
      }
    }
    return null;
  }

  /* The studio validates the scheme, but this is editor-supplied text landing in
     an href - a whitelist here costs nothing and closes the javascript: hole for
     good. */
  function safeHref(url) {
    if (!url) return '';
    return /^(https?:|mailto:)/i.test(String(url).trim()) ? String(url).trim() : '';
  }

  /* ---------------------------------------------------------------- helpers */

  function decodeEntities(node) {
    if (typeof node === 'string') {
      var slot = document.createElement('textarea');
      slot.innerHTML = node;
      return slot.value;
    }
    if (node && typeof node === 'object') {
      Object.keys(node).forEach(function (key) {
        node[key] = decodeEntities(node[key]);
      });
    }
    return node;
  }

  function escape(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (char) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
    });
  }
})();
